import React, { useEffect, useRef, useState } from 'react';
import { api, formatApiError } from '../lib/api';
import { Plus, Trash2, Edit3, Upload, ImageOff, AlertCircle, GripVertical, Check, Sparkles, Wand2, CheckSquare, Square, MoveRight, X as XIcon, Copy, FileSpreadsheet, Download } from 'lucide-react';
import {
    DndContext, closestCenter, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
    SortableContext, sortableKeyboardCoordinates, useSortable,
    horizontalListSortingStrategy, rectSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Modal } from '../lib/Modal';
import { useAuth } from '../lib/AuthContext';
import { hasPlanFeature, planFeatures } from '../lib/plans';
import { CATEGORY_EMOJI_OPTIONS, displayCategoryEmoji, suggestCategoryEmoji } from '../lib/categoryEmoji';
import { MENU_LANG_META } from '../lib/menuLocale';
import { MenuFlag } from '../components/MenuFlag';
import { MenuQrCard } from '../components/MenuQrCard';
import { MenuImportModal } from '../components/MenuImportModal';
import { MenuCopyFromLocationModal } from '../components/MenuCopyFromLocationModal';

function SortableCategory({ cat, active, onSelect, onEdit, onDelete }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '0.45rem 0.8rem',
        borderRadius: 999,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        border: active ? '1px solid var(--amber)' : '1px solid var(--border)',
        background: active ? 'rgba(255, 184, 51, 0.12)' : 'var(--surface-2)',
        color: active ? 'var(--amber)' : '#fff',
        fontWeight: active ? 600 : 500,
        transition: 'background 0.15s, border-color 0.15s',
    };
    // Whole chip is draggable: long-press (180ms) starts a drag, short tap selects.
    // Edit/delete buttons stopPropagation so they don't trigger select/drag.
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}
             onClick={onSelect} data-testid={`cat-${cat.id}`}>
            <GripVertical size={12} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
            <span style={{ fontSize: '1.05rem', lineHeight: 1 }}>{displayCategoryEmoji(cat)}</span>
            <span>{cat.name}</span>
            {cat.needs_kitchen === false && (
                <span title="Sare peste bucătărie — merge direct la ospătar"
                      aria-label="categorie fără bucătărie"
                      style={{ color: 'var(--neon-amber, #ffc857)', lineHeight: 1, fontSize: '0.95em' }}>⚡</span>
            )}
            <button type="button" className="btn btn-ghost"
                    style={{ padding: 4, marginLeft: 2, lineHeight: 0, touchAction: 'manipulation' }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); onEdit(); }}
                    title="Editează categorie">
                <Edit3 size={14} />
            </button>
            <button type="button" className="btn btn-ghost"
                    style={{ padding: 4, lineHeight: 0, touchAction: 'manipulation' }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    title="Șterge categorie">
                <Trash2 size={14} color="var(--neon-magenta)" />
            </button>
        </div>
    );
}

function SortableProduct({ prod, onEdit, onDelete, selectMode = false, selected = false, onToggleSelect }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: prod.id,
        disabled: selectMode,  // dragging is paused while picking items to move/delete
    });
    const inactive = prod.is_active === false;
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        // Dim the whole card when hidden from clients so the admin can scan the
        // grid and spot "off" items immediately.
        opacity: isDragging ? 0.5 : (inactive ? 0.55 : 1),
        position: 'relative',
        border: selected
            ? '2px solid var(--brand-orange)'
            : (inactive ? '1px dashed var(--border-strong)' : undefined),
        cursor: selectMode ? 'pointer' : undefined,
    };
    // Whole card is draggable on long-press (180ms via TouchSensor) — same UX as categories.
    // The image area + name + description are all valid grab targets on touch.
    const cardProps = selectMode
        ? { onClick: () => onToggleSelect?.(prod.id) }
        : { ...attributes, ...listeners };
    return (
        <div ref={setNodeRef} style={style} {...cardProps}
             className="panel panel-hover" data-testid={`product-${prod.id}`}>
            {selectMode && (
                <div style={{
                    position: 'absolute', top: 10, right: 10, zIndex: 2,
                    background: 'var(--surface)', borderRadius: 8,
                    width: 30, height: 30,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1px solid var(--border)',
                    color: selected ? 'var(--brand-orange)' : 'var(--text-faint)',
                }} data-testid={`product-check-${prod.id}`}>
                    {selected ? <CheckSquare size={20} strokeWidth={2.5} /> : <Square size={20} />}
                </div>
            )}
            {inactive && !selectMode && (
                <span
                    title="Produsul nu apare în meniul clienților"
                    style={{
                        position: 'absolute', top: 10, left: 10, zIndex: 2,
                        padding: '2px 8px', borderRadius: 999,
                        background: 'rgba(255, 46, 146, 0.15)',
                        border: '1px solid rgba(255, 46, 146, 0.45)',
                        color: 'var(--neon-magenta)',
                        fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.04em',
                    }}
                    data-testid={`product-inactive-${prod.id}`}
                >
                    ASCUNS
                </span>
            )}
            {prod.image_url ? (
                <div style={{ width: '100%', height: 130, borderRadius: 10, overflow: 'hidden', marginBottom: '0.7rem', background: 'var(--surface-2)' }}>
                    <img src={prod.image_url} alt={prod.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} data-testid={`product-thumb-${prod.id}`} />
                </div>
            ) : (
                <div style={{ width: '100%', height: 130, borderRadius: 10, background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(255,46,146,0.05))', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.7rem', color: 'var(--text-faint)' }}>
                    <ImageOff size={28} />
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem', gap: 4 }}>
                <span style={{ color: 'var(--text-faint)', flexShrink: 0 }}><GripVertical size={14} /></span>
                <div style={{ fontWeight: 600, fontSize: '1.05rem', flex: 1 }}>{prod.name}</div>
                <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" className="btn btn-ghost btn-sm"
                            style={{ touchAction: 'manipulation' }}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); onEdit(); }}
                            data-testid={`edit-product-${prod.id}`}>
                        <Edit3 size={13} />
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm"
                            style={{ touchAction: 'manipulation' }}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); onDelete(); }}>
                        <Trash2 size={13} color="var(--neon-magenta)" />
                    </button>
                </div>
            </div>
            {prod.description && <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.6rem', lineHeight: 1.4 }}>{prod.description}</div>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {prod.variants.map(v => (<span key={v.id} className="badge badge-amber">{v.name} • {v.price} RON</span>))}
            </div>
            {prod.tags?.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                    {prod.tags.map(t => <span key={t} className="badge badge-teal">{t}</span>)}
                </div>
            )}
        </div>
    );
}

export default function MenuPage() {
    const { restaurant } = useAuth();
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [activeCat, setActiveCat] = useState(null);
    const [showCatModal, setShowCatModal] = useState(false);
    const [showProdModal, setShowProdModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [editingCat, setEditingCat] = useState(null);
    const [editingProd, setEditingProd] = useState(null);
    const [uploadStatus, setUploadStatus] = useState({ configured: false, checked: false });
    const [menuLanguages, setMenuLanguages] = useState(['ro', 'en']);
    // Local mirror of the AI menu import quota so the badge on the trigger
    // button updates immediately after a draft is generated, without waiting
    // for the auth context (`/auth/me`) to be re-fetched.
    const [importUsage, setImportUsage] = useState(null);
    const { refresh: refreshAuth, myLocations } = useAuth();
    // Bulk selection mode for product cards: pick several, then move-to-category
    // or delete them in one shot. Drag-to-reorder is suspended while active so
    // taps don't accidentally trigger drags.
    const [selectMode, setSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkMoveTarget, setBulkMoveTarget] = useState('');
    const [bulkBusy, setBulkBusy] = useState(false);
    const [excelBusy, setExcelBusy] = useState(false);
    const excelInputRef = useRef(null);

    const reload = async () => {
        const [c, p] = await Promise.all([api.get('/r/categories'), api.get('/r/products')]);
        setCategories(c.data.items);
        setProducts(p.data.items);
        if (!activeCat && c.data.items.length) setActiveCat(c.data.items[0].id);
    };

    useEffect(() => {
        reload();
        api.get('/r/uploads/status').then(r => setUploadStatus({ ...r.data, checked: true })).catch(() => {});
        api.get('/r/restaurant/settings').then(r => {
            setMenuLanguages(r.data.menu_languages?.enabled || ['ro', 'en']);
        }).catch(() => {});
    }, []);  // eslint-disable-line

    // Hydrate import quota from the auth context once it's available, but let
    // the modal overwrite it via `onUsageChange` after each draft generation.
    useEffect(() => {
        if (importUsage) return;
        const pf = planFeatures(restaurant);
        if (!pf) return;
        setImportUsage({
            used_this_month: pf.usage?.ai_menu_import_this_month ?? 0,
            quota_monthly: pf.limits?.ai_menu_import_quota_monthly,
        });
    }, [restaurant, importUsage]);

    // Clear bulk-selection when the user switches categories — selecting items
    // from one category and moving while viewing another would be confusing.
    useEffect(() => {
        setSelectMode(false);
        setSelectedIds(new Set());
        setBulkMoveTarget('');
    }, [activeCat]);

    // Split main categories (no parent) from subcategories. Order respected for
    // main categories; subcategories are rendered in their stored order under
    // their parent (creation order if `order` is identical / unset).
    const mainCategories = categories.filter(c => !c.parent_id);
    const subsByParent = categories.reduce((acc, c) => {
        if (c.parent_id) {
            (acc[c.parent_id] = acc[c.parent_id] || []).push(c);
        }
        return acc;
    }, {});
    Object.values(subsByParent).forEach(arr => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));

    const activeCatObj = categories.find(c => c.id === activeCat) || null;
    const activeIsMain = activeCatObj && !activeCatObj.parent_id;
    const activeMainId = activeCatObj
        ? (activeCatObj.parent_id || activeCatObj.id)
        : null;
    const activeMainHasSubs = activeMainId ? Boolean(subsByParent[activeMainId]?.length) : false;

    // Mixed mode: a main category can hold direct products even when it also
    // has subcategories. The product grid shows only products attached
    // directly to whatever's selected (main or sub) — sub products show up
    // when the user clicks that sub's pill.
    const filtered = products.filter(p => activeCat && p.category_id === activeCat);

    // All categories are valid product targets — mains with or without subs,
    // and every subcategory. Subcategories get their parent name prefixed
    // so the dropdown reads "Băuturi › Calde" instead of just "Calde".
    const productCategoryChoices = categories.map(c => {
        if (!c.parent_id) return c;
        const parent = categories.find(p => p.id === c.parent_id);
        return parent ? { ...c, name: `${parent.name} › ${c.name}` } : c;
    });

    const openProductModal = (prod = null) => {
        const defaultCat = activeCat || prod?.category_id || '';
        setEditingProd(prod || { category_id: defaultCat, name: '', description: '', tags: [], allergens: [], is_active: true, variants: [] });
        setShowProdModal(true);
    };

    const openSubcategoryModal = (parentId) => {
        // Inherit kitchen routing from the parent. If "Bar" is flagged as no-kitchen
        // (drinks), every subcategory under it should default to the same so the
        // owner doesn't have to remember to uncheck it for each one.
        const parent = categories.find(c => c.id === parentId);
        setEditingCat({
            parent_id: parentId,
            needs_kitchen: parent ? parent.needs_kitchen !== false : true,
        });
        setShowCatModal(true);
    };

    const saveCategory = async (data) => {
        const payload = {
            name: data.name,
            emoji: data.emoji,
            order: data.order || 0,
            image_url: data.image_url || null,
            translations: data.translations || {},
            needs_kitchen: data.needs_kitchen !== false,
            parent_id: data.parent_id || null,
        };
        try {
            if (editingCat?.id) await api.put(`/r/categories/${editingCat.id}`, payload);
            else await api.post('/r/categories', payload);
            setShowCatModal(false); setEditingCat(null); reload();
        } catch (e) {
            alert(formatApiError(e));
            throw e;
        }
    };

    const saveProduct = async (data, imageFile = null) => {
        const payload = {
            category_id: data.category_id,
            name: data.name, description: data.description || '',
            image_url: data.image_url || null,
            tags: data.tags || [], allergens: data.allergens || [], ingredients: data.ingredients || [],
            is_active: data.is_active !== false,
            variants: (data.variants || []).map(v => ({
                ...(v.id ? { id: v.id } : {}),
                name: v.name,
                price: parseFloat(v.price) || 0,
                weight: v.weight || null,
            })),
            translations: data.translations || {},
            ...(data.nutrition ? { nutrition: data.nutrition } : {}),
        };
        try {
            if (data.id) {
                await api.put(`/r/products/${data.id}`, payload);
            } else {
                const { data: created } = await api.post('/r/products', payload);
                if (imageFile) {
                    const fd = new FormData();
                    fd.append('file', imageFile);
                    await api.post(`/r/products/${created.id}/image`, fd, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    });
                }
            }
            setShowProdModal(false);
            setEditingProd(null);
            reload().catch(() => {});
        } catch (e) {
            alert(formatApiError(e));
            throw e;
        }
    };

    const deleteCategory = async (id) => { if (window.confirm('Ștergi categoria și toate produsele ei?')) { await api.delete(`/r/categories/${id}`); reload(); } };
    const deleteProduct = async (id) => { if (window.confirm('Ștergi produsul?')) { await api.delete(`/r/products/${id}`); reload(); } };

    const exitSelectMode = () => {
        setSelectMode(false);
        setSelectedIds(new Set());
        setBulkMoveTarget('');
    };

    const toggleProductSelection = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectAllVisible = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            filtered.forEach((p) => next.add(p.id));
            return next;
        });
    };

    const bulkMove = async () => {
        if (!bulkMoveTarget || selectedIds.size === 0) return;
        setBulkBusy(true);
        try {
            await api.patch('/r/products/bulk/category', {
                product_ids: Array.from(selectedIds),
                category_id: bulkMoveTarget,
            });
            await reload();
            exitSelectMode();
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setBulkBusy(false);
        }
    };

    const fetchExcel = async (url, filename) => {
        setExcelBusy(true);
        try {
            const res = await api.get(url, { responseType: 'blob' });
            const blob = new Blob([res.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const dlUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = dlUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(dlUrl);
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setExcelBusy(false);
        }
    };

    const downloadExcelTemplate = () => fetchExcel('/r/menu/template.xlsx', 'model-meniu-clickmeniu.xlsx');
    const downloadExcelMenu = () => fetchExcel('/r/menu/export.xlsx', `meniu-${restaurant?.slug || 'export'}.xlsx`);

    const handleExcelFile = async (file) => {
        if (!file) return;
        const replace = window.confirm(
            'Cum vrei să imporți fișierul Excel?\n\n' +
            'OK = ÎNLOCUIEȘTE meniul actual (șterge tot apoi adaugă)\n' +
            'Anulează = ADAUGĂ la meniul existent',
        );
        setExcelBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('replace', replace ? 'true' : 'false');
            const { data } = await api.post('/r/menu/import-excel', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 120000,
            });
            await reload();
            alert(
                `Importat din Excel: ${data.created_categories} categorii, ${data.created_subcategories} subcategorii, ${data.created_products} produse. ` +
                'Traducerile se generează în background.',
            );
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setExcelBusy(false);
            if (excelInputRef.current) excelInputRef.current.value = '';
        }
    };

    const bulkDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Ștergi ${selectedIds.size} produse selectate? Această acțiune nu poate fi anulată.`)) return;
        setBulkBusy(true);
        try {
            await Promise.all(
                Array.from(selectedIds).map((id) => api.delete(`/r/products/${id}`).catch(() => null)),
            );
            await reload();
            exitSelectMode();
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setBulkBusy(false);
        }
    };

    // Separate Mouse + Touch sensors so taps/scrolls on touch don't get hijacked as drag.
    // MouseSensor: small distance (6px) feels snappy on desktop.
    // TouchSensor: short hold (180ms) — long enough to distinguish from a tap/scroll, short
    // enough to feel responsive. Tolerance 8px keeps the press-and-hold robust to micro-jitter.
    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleCategoryDragEnd = async (e) => {
        // Only main categories are sortable in the top row. Subcategories keep
        // their stored order — we'd need a second SortableContext per parent
        // to reorder them and that's out of scope for the MVP.
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIdx = mainCategories.findIndex(c => c.id === active.id);
        const newIdx = mainCategories.findIndex(c => c.id === over.id);
        if (oldIdx === -1 || newIdx === -1) return;
        const sortedMains = arrayMove(mainCategories, oldIdx, newIdx);
        const otherSubs = categories.filter(c => c.parent_id);
        setCategories([...sortedMains, ...otherSubs]);
        await api.put('/r/categories/reorder', { items: sortedMains.map((c, i) => ({ id: c.id, order: i })) });
    };

    const handleProductDragEnd = async (e) => {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const visible = products.filter(p => !activeCat || p.category_id === activeCat);
        const oldIdx = visible.findIndex(p => p.id === active.id);
        const newIdx = visible.findIndex(p => p.id === over.id);
        const reordered = arrayMove(visible, oldIdx, newIdx);
        // merge back
        const others = products.filter(p => p.category_id !== activeCat);
        setProducts([...others, ...reordered]);
        await api.put('/r/products/reorder', { items: reordered.map((p, i) => ({ id: p.id, order: i })) });
    };

    return (
        <div className="fade-in-up" data-testid="menu-page">
            <div className="kicker">Meniu</div>
            <h1 className="headline" style={{ fontSize: '2.4rem', margin: '0.3rem 0 1.4rem' }}>Compune-ți meniul.</h1>

            {!hasPlanFeature(restaurant, 'table_management') && (
                <MenuQrCard restaurantName={restaurant?.name} />
            )}

            {uploadStatus.checked && !uploadStatus.configured && (
                <div className="panel" style={{ marginBottom: '1.2rem', background: 'rgba(0, 217, 255, 0.06)', border: '1px solid rgba(0, 217, 255, 0.25)', display: 'flex', gap: '0.8rem', alignItems: 'start' }} data-testid="supabase-banner">
                    <AlertCircle size={18} color="var(--neon-cyan)" style={{ flexShrink: 0, marginTop: 2 }} />
                    <div style={{ fontSize: '0.9rem', lineHeight: 1.55 }}>
                        <strong style={{ color: 'var(--neon-cyan)' }}>Upload-ul de poze pentru meniu nu e configurat încă.</strong>
                        <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
                            Setează <code style={{ color: 'var(--amber)' }}>SUPABASE_URL</code>, <code style={{ color: 'var(--amber)' }}>SUPABASE_SERVICE_ROLE_KEY</code> și <code style={{ color: 'var(--amber)' }}>SUPABASE_BUCKET</code> în <code style={{ color: 'var(--amber)' }}>backend/.env</code>, apoi restart backend. Bucket-ul trebuie marcat <em>Public</em>.
                        </div>
                    </div>
                </div>
            )}

            {/* Categories — main categories as pills (drag-sortable); when one of
                them is selected and has subcategories, the second row shows
                the subcategory pills underneath. */}
            <div className="panel" style={{ marginBottom: '1.2rem', padding: '0.7rem 0.9rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem', flexWrap: 'wrap', gap: 8 }}>
                    <div className="kicker">Categorii</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={downloadExcelTemplate}
                            disabled={excelBusy}
                            title="Descarcă modelul de fișier Excel cu instrucțiuni și exemple"
                            data-testid="template-excel"
                        >
                            <FileSpreadsheet size={14} /> Model meniu Excel
                        </button>
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={downloadExcelMenu}
                            disabled={excelBusy}
                            title="Descarcă meniul actual al locației ca Excel"
                            data-testid="export-excel"
                        >
                            <Download size={14} /> Export meniu Excel
                        </button>
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => excelInputRef.current?.click()}
                            disabled={excelBusy}
                            title="Importă meniul dintr-un Excel (gratuit)"
                            data-testid="import-excel"
                        >
                            <Upload size={14} /> {excelBusy ? 'Se procesează...' : 'Import meniu Excel'}
                        </button>
                        <input
                            ref={excelInputRef}
                            type="file"
                            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                            style={{ display: 'none' }}
                            onChange={(e) => handleExcelFile(e.target.files?.[0])}
                            data-testid="excel-file-input"
                        />
                        {(myLocations?.length || 0) >= 2 && (
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setShowCopyModal(true)}
                                title="Copiază meniul dintr-o altă locație a ta (gratuit)"
                                data-testid="copy-menu-from-location"
                            >
                                <Copy size={14} /> Copiază din altă locație
                            </button>
                        )}
                        {hasPlanFeature(restaurant, 'ai_menu_import') && (() => {
                            const used = importUsage?.used_this_month ?? 0;
                            const quota = importUsage?.quota_monthly;
                            const out = quota != null && used >= quota;
                            return (
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setShowImportModal(true)}
                                    disabled={out}
                                    title={out ? 'Ai atins limita de importuri pe luna aceasta' : 'Importă un meniu PDF/poză cu AI'}
                                    data-testid="import-menu"
                                >
                                    <Wand2 size={14} /> Importă meniu (AI)
                                    {quota != null && (
                                        <span style={{ color: 'var(--text-faint)', fontSize: '0.78rem', marginLeft: 6 }}>
                                            {used}/{quota}
                                        </span>
                                    )}
                                </button>
                            );
                        })()}
                        <button className="btn btn-secondary btn-sm" onClick={() => { setEditingCat(null); setShowCatModal(true); }} data-testid="add-category">
                            <Plus size={14} /> Adaugă categorie
                        </button>
                    </div>
                </div>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
                    <SortableContext items={mainCategories.map(c => c.id)} strategy={horizontalListSortingStrategy}>
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 6,
                            overflowX: 'auto',
                            paddingBottom: 4,
                        }}>
                            {mainCategories.map(c => (
                                <SortableCategory key={c.id} cat={c}
                                    active={activeCat === c.id || activeCatObj?.parent_id === c.id}
                                    onSelect={() => setActiveCat(c.id)}
                                    onEdit={() => { setEditingCat(c); setShowCatModal(true); }}
                                    onDelete={() => deleteCategory(c.id)} />
                            ))}
                            {!mainCategories.length && (
                                <div style={{ color: 'var(--text-faint)', fontSize: '0.88rem', padding: '0.5rem' }}>
                                    Nicio categorie. Adaugă prima.
                                </div>
                            )}
                        </div>
                    </SortableContext>
                </DndContext>

                {activeMainId && (subsByParent[activeMainId]?.length || activeIsMain) && (
                    <div style={{
                        marginTop: '0.65rem',
                        paddingTop: '0.6rem',
                        borderTop: '1px dashed var(--border-faint)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                    }}>
                        <span className="kicker" style={{ marginRight: 4 }}>Subcategorii</span>
                        {(subsByParent[activeMainId] || []).map(sub => {
                            const isActive = activeCat === sub.id;
                            return (
                                <div key={sub.id}
                                     onClick={() => setActiveCat(sub.id)}
                                     data-testid={`subcat-${sub.id}`}
                                     style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        padding: '0.32rem 0.65rem',
                                        borderRadius: 999,
                                        cursor: 'pointer',
                                        fontSize: '0.85rem',
                                        border: isActive ? '1px solid var(--neon-teal)' : '1px solid var(--border-faint)',
                                        background: isActive ? 'rgba(45, 212, 191, 0.10)' : 'var(--surface-2)',
                                        color: isActive ? 'var(--neon-teal)' : 'var(--text-muted)',
                                        fontWeight: isActive ? 600 : 500,
                                     }}>
                                    {displayCategoryEmoji(sub)} <span>{sub.name}</span>
                                    <button type="button"
                                            onClick={(e) => { e.stopPropagation(); setEditingCat(sub); setShowCatModal(true); }}
                                            className="btn btn-ghost" style={{ padding: 2, lineHeight: 0 }}
                                            title="Editează subcategorie">
                                        <Edit3 size={12} />
                                    </button>
                                    <button type="button"
                                            onClick={(e) => { e.stopPropagation(); deleteCategory(sub.id); }}
                                            className="btn btn-ghost" style={{ padding: 2, lineHeight: 0 }}
                                            title="Șterge subcategorie">
                                        <Trash2 size={12} color="var(--neon-magenta)" />
                                    </button>
                                </div>
                            );
                        })}
                        {activeIsMain && (
                            <button type="button" className="btn btn-ghost btn-sm"
                                    onClick={() => openSubcategoryModal(activeMainId)}
                                    data-testid={`add-subcat-${activeMainId}`}
                                    style={{ fontSize: '0.82rem', padding: '0.3rem 0.6rem' }}>
                                <Plus size={12} /> Subcategorie
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Products — grid larg jos, în același tip de panel ca Categoriile */}
            <div className="panel" style={{ padding: '0.9rem 1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.9rem', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                        <div className="kicker">Produse</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                            {!activeCat
                                ? 'Alege o categorie de sus pentru a vedea produsele.'
                                : (activeIsMain && activeMainHasSubs
                                    ? `${filtered.length} produs(e) direct(e) în această categorie · subcategoriile au produsele lor`
                                    : `${filtered.length} produs(e) · trage pentru a reordona`)}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {filtered.length > 0 && (
                            <button
                                className={`btn btn-sm ${selectMode ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}
                                data-testid="toggle-select-mode"
                            >
                                {selectMode ? <><XIcon size={14} /> Ieși din selecție</> : <><CheckSquare size={14} /> Selectează</>}
                            </button>
                        )}
                        <button className="btn btn-primary" disabled={!activeCat || selectMode} onClick={() => openProductModal()} data-testid="add-product">
                            <Plus size={16} /> Adaugă produs
                        </button>
                    </div>
                </div>

                {selectMode && (
                    <div style={{
                        marginBottom: '0.9rem',
                        padding: '0.75rem 0.9rem',
                        borderRadius: 10,
                        background: 'rgba(242, 101, 34, 0.06)',
                        border: '1px solid rgba(242, 101, 34, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        flexWrap: 'wrap',
                    }} data-testid="bulk-action-bar">
                        <strong style={{ color: 'var(--amber)', whiteSpace: 'nowrap' }}>
                            {selectedIds.size} selectate
                        </strong>
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={selectAllVisible}
                            disabled={bulkBusy || filtered.every((p) => selectedIds.has(p.id))}
                        >
                            Selectează tot
                        </button>
                        <div style={{ flex: 1, minWidth: 200, display: 'flex', gap: 6, alignItems: 'center' }}>
                            <MoveRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                            <select
                                className="input"
                                value={bulkMoveTarget}
                                onChange={(e) => setBulkMoveTarget(e.target.value)}
                                disabled={bulkBusy || selectedIds.size === 0}
                                style={{ flex: 1, padding: '0.45rem 0.6rem', fontSize: '0.88rem' }}
                                data-testid="bulk-move-target"
                            >
                                <option value="">Mută în categoria...</option>
                                {productCategoryChoices.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {displayCategoryEmoji(c)} {c.name}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={bulkMove}
                                disabled={bulkBusy || !bulkMoveTarget || selectedIds.size === 0}
                                data-testid="bulk-move-confirm"
                            >
                                {bulkBusy ? <span className="spinner" /> : <Check size={14} />} Mută
                            </button>
                        </div>
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={bulkDelete}
                            disabled={bulkBusy || selectedIds.size === 0}
                            data-testid="bulk-delete"
                            style={{ color: 'var(--neon-magenta)' }}
                        >
                            <Trash2 size={14} /> Șterge
                        </button>
                    </div>
                )}
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleProductDragEnd}>
                    <SortableContext items={filtered.map(p => p.id)} strategy={rectSortingStrategy}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
                            {filtered.map(p => (
                                <SortableProduct key={p.id} prod={p}
                                    onEdit={() => openProductModal(p)}
                                    onDelete={() => deleteProduct(p.id)}
                                    selectMode={selectMode}
                                    selected={selectedIds.has(p.id)}
                                    onToggleSelect={toggleProductSelection} />
                            ))}
                            {!filtered.length && activeCat && (
                                <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--text-muted)', padding: '1.5rem 0' }}>
                                    {activeIsMain && activeMainHasSubs
                                        ? 'Niciun produs direct aici. Adaugă unul aici sau folosește subcategoriile.'
                                        : 'Niciun produs în această categorie. Adaugă primul produs.'}
                                </div>
                            )}
                        </div>
                    </SortableContext>
                </DndContext>
            </div>

            <MenuCopyFromLocationModal
                open={showCopyModal}
                onClose={() => setShowCopyModal(false)}
                locations={myLocations || []}
                currentRestaurantId={restaurant?.id}
                onCopied={(result) => {
                    reload();
                    alert(`Copiat: ${result.copied_categories} categorii, ${result.copied_products} produse.`);
                }}
            />
            <MenuImportModal
                open={showImportModal}
                onClose={() => setShowImportModal(false)}
                onImported={(result) => {
                    reload();
                    // Pull fresh plan_features into the auth context so the quota
                    // badge stays correct across remounts / page navigation.
                    refreshAuth?.({ silent: true }).catch(() => {});
                    alert(`Importat: ${result.created_categories} categorii, ${result.created_products} produse. Traducerile se generează în background.`);
                }}
                usage={importUsage}
                onUsageChange={setImportUsage}
            />
            <Modal open={showCatModal} title={editingCat?.id ? 'Editează categorie' : (editingCat?.parent_id ? 'Subcategorie nouă' : 'Categorie nouă')} onClose={() => { setShowCatModal(false); setEditingCat(null); }}>
                <CategoryForm initial={editingCat} mainCategories={mainCategories} subsByParent={subsByParent} menuLanguages={menuLanguages} onSave={saveCategory} />
            </Modal>
            <Modal open={showProdModal} title={editingProd?.id ? 'Editează produs' : 'Produs nou'} onClose={() => { setShowProdModal(false); setEditingProd(null); }}>
                <ProductForm initial={editingProd} categories={productCategoryChoices} menuLanguages={menuLanguages} uploadConfigured={uploadStatus.configured}
                             onImageChanged={() => reload()}
                             onNutritionGenerated={() => reload()}
                             onSave={saveProduct} />
            </Modal>
        </div>
    );
}

function MenuTranslationFields({ menuLanguages, form, setForm, mode = 'category', variants = [] }) {
    const extraLangs = (menuLanguages || []).filter((code) => code !== 'ro');
    const [open, setOpen] = useState(false);
    if (!extraLangs.length) return null;
    const isProduct = mode === 'product';

    const getTr = (lang) => form.translations?.[lang] || {};
    const setTr = (lang, patch) => {
        setForm((prev) => {
            const current = prev.translations?.[lang] || {};
            const next = { ...current, ...patch };
            if (patch.variants) {
                next.variants = { ...(current.variants || {}), ...patch.variants };
            }
            return {
                ...prev,
                translations: {
                    ...(prev.translations || {}),
                    [lang]: next,
                },
            };
        });
    };

    return (
        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
            <button type="button"
                    onClick={() => setOpen((o) => !o)}
                    data-testid="translations-toggle"
                    style={{
                        background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                        color: 'var(--text-muted)', fontSize: '0.88rem', fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>
                <span>{open ? '▾' : '▸'}</span>
                <span>Corecturi manuale traduceri (opțional)</span>
                <span style={{ marginLeft: 'auto', fontSize: '0.74rem', fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--text-faint)' }}>
                    {extraLangs.length} limb{extraLangs.length === 1 ? 'ă' : 'i'} · auto-traduse
                </span>
            </button>
            {!open && (
                <p style={{ color: 'var(--text-faint)', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
                    Traducerile se generează automat din textul în română. Deschide doar dacă vrei să corectezi o formulare specifică.
                </p>
            )}
            {open && (
                <>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', margin: '0.6rem 0 0.8rem' }}>
                        Traducerile se generează automat din textul în română. Poți suprascrie manual aici dacă vrei o formulare specială.
                    </p>
                    {extraLangs.map((lang) => {
                const meta = MENU_LANG_META[lang];
                const tr = getTr(lang);
                return (
                    <div key={lang} style={{ marginBottom: '0.9rem', padding: '0.75rem', borderRadius: 10, background: 'var(--surface-2)' }}>
                        <div style={{ fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <MenuFlag code={lang} size={32} />
                            <span>{meta?.native || lang}</span>
                        </div>
                        <div style={{ marginBottom: 8 }}>
                            <label className="label">Nume</label>
                            <input className="input" value={tr.name || ''} onChange={(e) => setTr(lang, { name: e.target.value })} />
                        </div>
                        {isProduct ? (
                            <>
                                <div style={{ marginBottom: 8 }}>
                                    <label className="label">Descriere</label>
                                    <textarea className="textarea" rows={2} value={tr.description || ''} onChange={(e) => setTr(lang, { description: e.target.value })} data-testid={`tr-desc-${lang}`} />
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                    <label className="label">Ingrediente (virgulă)</label>
                                    <input className="input" value={(tr.ingredients || []).join(', ')} onChange={(e) => setTr(lang, { ingredients: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} data-testid={`tr-ingredients-${lang}`} />
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                    <label className="label">Tag-uri (virgulă)</label>
                                    <input className="input" value={(tr.tags || []).join(', ')} onChange={(e) => setTr(lang, { tags: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
                                </div>
                                {variants.filter((v) => v.id).map((v) => (
                                    <div key={v.id} style={{ marginBottom: 6 }}>
                                        <label className="label">Variantă: {v.name || v.id.slice(0, 6)}</label>
                                        <input
                                            className="input"
                                            value={tr.variants?.[v.id] || ''}
                                            onChange={(e) => setTr(lang, { variants: { [v.id]: e.target.value } })}
                                        />
                                    </div>
                                ))}
                            </>
                        ) : null}
                    </div>
                );
            })}
                </>
            )}
        </div>
    );
}

function CategoryForm({ initial, mainCategories = [], subsByParent = {}, menuLanguages, onSave }) {
    const [form, setForm] = useState({
        name: '', emoji: '', order: 0, image_url: '', translations: {},
        needs_kitchen: true,
        parent_id: '',
        ...(initial || {}),
        needs_kitchen: initial?.needs_kitchen !== false,
        parent_id: initial?.parent_id || '',
    });
    const [emojiTouched, setEmojiTouched] = useState(Boolean(initial?.emoji));

    // A category that currently has its own subcategories cannot be turned into
    // a subcategory itself — block the parent dropdown for it. Likewise, when
    // editing, exclude self from the parent options.
    const selfHasChildren = Boolean(initial?.id && subsByParent[initial.id]?.length);
    const parentOptions = mainCategories.filter(c => c.id !== initial?.id);

    useEffect(() => {
        if (emojiTouched || !form.name.trim()) return;
        setForm(prev => ({ ...prev, emoji: suggestCategoryEmoji(prev.name) }));
    }, [form.name, emojiTouched]);

    const pickEmoji = (emoji) => {
        setEmojiTouched(true);
        setForm(prev => ({ ...prev, emoji }));
    };

    return (
        <div>
            <div style={{ marginBottom: '0.9rem' }}>
                <label className="label">Nume</label>
                <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name" />
            </div>
            <div style={{ marginBottom: '0.9rem' }}>
                <label className="label" htmlFor="cat-parent">Categorie părinte (opțional)</label>
                <select
                    id="cat-parent"
                    className="input"
                    value={form.parent_id || ''}
                    onChange={(e) => {
                        const newParentId = e.target.value;
                        // Adopt the new parent's kitchen-routing flag so a
                        // subcategory inherits the parent's setting by default
                        // (e.g. Bar → no-kitchen → all subs default to no-kitchen).
                        // Only auto-sync when a parent is being set, not when
                        // promoting to a main category.
                        const parent = newParentId
                            ? parentOptions.find(c => c.id === newParentId)
                            : null;
                        setForm(prev => ({
                            ...prev,
                            parent_id: newParentId,
                            needs_kitchen: parent ? parent.needs_kitchen !== false : prev.needs_kitchen,
                        }));
                    }}
                    disabled={selfHasChildren}
                    data-testid="cat-parent"
                >
                    <option value="">— Categorie principală</option>
                    {parentOptions.map(c => (
                        <option key={c.id} value={c.id}>
                            {displayCategoryEmoji(c)} {c.name}
                        </option>
                    ))}
                </select>
                <div style={{ color: 'var(--text-faint)', fontSize: '0.78rem', marginTop: 6 }}>
                    {selfHasChildren
                        ? 'Această categorie are deja subcategorii — nu o poți transforma în subcategorie.'
                        : 'Lasă gol pentru categorie principală. Alege o categorie părinte pentru a o transforma în subcategorie.'}
                </div>
            </div>
            <div style={{
                marginBottom: '0.9rem',
                padding: '0.75rem 0.9rem',
                borderRadius: 10,
                border: '1px solid var(--border-faint)',
                background: 'var(--bg-faint, rgba(255,255,255,0.02))',
            }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={form.needs_kitchen !== false}
                        onChange={(e) => setForm({ ...form, needs_kitchen: e.target.checked })}
                        style={{ marginTop: 3 }}
                        data-testid="cat-needs-kitchen"
                    />
                    <span>
                        <strong style={{ display: 'block' }}>🍳 Produsele trec prin bucătărie</strong>
                        <span style={{ color: 'var(--text-faint)', fontSize: '0.82rem', lineHeight: 1.4 }}>
                            Debifează pentru băuturi, deserturi gata făcute, cafele etc. — produsele
                            vor sări peste bucătărie și vor ajunge direct la ospătar pentru servire.
                        </span>
                    </span>
                </label>
            </div>
            <div style={{ marginBottom: '0.9rem' }}>
                <label className="label">Emoticon</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    {CATEGORY_EMOJI_OPTIONS.map(emoji => (
                        <button
                            key={emoji}
                            type="button"
                            className={`btn btn-sm ${form.emoji === emoji ? 'btn-primary' : 'btn-secondary'}`}
                            style={{ minWidth: 42, fontSize: '1.2rem', padding: '0.35rem 0.5rem' }}
                            onClick={() => pickEmoji(emoji)}
                            data-testid={`cat-emoji-${emoji}`}
                        >
                            {emoji}
                        </button>
                    ))}
                </div>
                <input
                    className="input"
                    value={form.emoji}
                    onChange={(e) => { setEmojiTouched(true); setForm({ ...form, emoji: e.target.value }); }}
                    placeholder="🍕"
                    data-testid="cat-emoji"
                />
                <div style={{ color: 'var(--text-faint)', fontSize: '0.78rem', marginTop: 6 }}>
                    Se sugerează automat după nume. Poți alege alt emoticon din grilă.
                </div>
            </div>
            <MenuTranslationFields menuLanguages={menuLanguages} form={form} setForm={setForm} />
            <button className="btn btn-primary" style={{ width: '100%', marginTop: '0.9rem' }} onClick={() => onSave({ ...form, emoji: form.emoji || suggestCategoryEmoji(form.name) })} disabled={!form.name} data-testid="cat-save">Salvează</button>
        </div>
    );
}

function productUsesVariants(prod) {
    if (!prod?.variants?.length) return false;
    if (prod.variants.length > 1) return true;
    const name = (prod.variants[0].name || '').trim();
    return name && !['Porție', 'Portie', 'Standard'].includes(name);
}

// Tag-uri populare pe care restaurantul le poate adăuga rapid cu un click. Lista e
// optimizată pentru ce caută clienții mâncărurilor (filtre comune + sezoniere).
const POPULAR_TAGS = [
    { id: 'vegetarian', label: 'Vegetarian', icon: '🥗' },
    { id: 'vegan', label: 'Vegan', icon: '🌱' },
    { id: 'picant', label: 'Picant', icon: '🌶️' },
    { id: 'fara_gluten', label: 'Fără gluten', icon: '🚫' },
    { id: 'bio', label: 'Bio', icon: '🌿' },
    { id: 'casa', label: 'Specialitatea casei', icon: '⭐' },
    { id: 'recomandat', label: 'Recomandat chef', icon: '👨‍🍳' },
    { id: 'nou', label: 'Nou în meniu', icon: '✨' },
    { id: 'popular', label: 'Popular', icon: '🔥' },
    { id: 'traditional', label: 'Tradițional', icon: '🇷🇴' },
    { id: 'local', label: 'Local', icon: '🏡' },
    { id: 'light', label: 'Light', icon: '🪶' },
    { id: 'proaspat', label: 'Proaspăt', icon: '🌅' },
    { id: 'copii', label: 'Pentru copii', icon: '🧒' },
    { id: 'halal', label: 'Halal', icon: '🕌' },
    { id: 'kosher', label: 'Kosher', icon: '✡️' },
];
const TAG_LABEL_TO_ID = Object.fromEntries(POPULAR_TAGS.map((t) => [t.label.toLowerCase(), t.id]));
const TAG_ID_TO_LABEL = Object.fromEntries(POPULAR_TAGS.map((t) => [t.id, t.label]));

function parseTags(raw) {
    const list = Array.isArray(raw) ? raw : (typeof raw === 'string' ? raw.split(',') : []);
    const ids = new Set();
    const custom = [];
    list.forEach((item) => {
        const s = String(item || '').trim();
        if (!s) return;
        const lower = s.toLowerCase();
        if (TAG_ID_TO_LABEL[lower]) ids.add(lower);
        else if (TAG_LABEL_TO_ID[lower]) ids.add(TAG_LABEL_TO_ID[lower]);
        else custom.push(s);
    });
    return { ids, custom };
}


// Cei 14 alergeni alimentari obligatorii conform Regulamentului UE 1169/2011 (Anexa II).
// Restaurantul dă click să-i adauge — fără să-i scrie. Order alphabetic în română.
const EU_ALLERGENS = [
    { id: 'gluten', label: 'Gluten', icon: '🌾', desc: 'Cereale care conțin gluten (grâu, secară, orz, ovăz, spelt, kamut)' },
    { id: 'crustacee', label: 'Crustacee', icon: '🦐', desc: 'Crustacee și produse derivate' },
    { id: 'oua', label: 'Ouă', icon: '🥚', desc: 'Ouă și produse derivate' },
    { id: 'peste', label: 'Pește', icon: '🐟', desc: 'Pește și produse derivate' },
    { id: 'arahide', label: 'Arahide', icon: '🥜', desc: 'Arahide și produse derivate' },
    { id: 'soia', label: 'Soia', icon: '🫘', desc: 'Soia și produse derivate' },
    { id: 'lapte', label: 'Lapte', icon: '🥛', desc: 'Lapte și produse derivate (inclusiv lactoză)' },
    { id: 'fructe_coaja', label: 'Fructe cu coajă', icon: '🌰', desc: 'Nuci, alune, migdale, fistic, caju etc.' },
    { id: 'telina', label: 'Țelină', icon: '🥬', desc: 'Țelină și produse derivate' },
    { id: 'mustar', label: 'Muștar', icon: '🌭', desc: 'Muștar și produse derivate' },
    { id: 'susan', label: 'Susan', icon: '🌱', desc: 'Susan și produse derivate' },
    { id: 'sulfiti', label: 'Sulfiți', icon: '🧪', desc: 'Dioxid de sulf și sulfiți (>10mg/kg sau >10mg/l)' },
    { id: 'lupin', label: 'Lupin', icon: '🌿', desc: 'Lupin și produse derivate' },
    { id: 'moluste', label: 'Moluște', icon: '🦪', desc: 'Moluște și produse derivate' },
];
const ALLERGEN_LABEL_TO_ID = Object.fromEntries(EU_ALLERGENS.map((a) => [a.label.toLowerCase(), a.id]));
const ALLERGEN_ID_TO_LABEL = Object.fromEntries(EU_ALLERGENS.map((a) => [a.id, a.label]));

// Convert legacy string allergens (e.g. "gluten, lapte" or array of free text) to a Set
// of standard IDs + leftover custom labels. Keeps backward compat with old data.
function parseAllergens(raw) {
    const list = Array.isArray(raw) ? raw : (typeof raw === 'string' ? raw.split(',') : []);
    const ids = new Set();
    const custom = [];
    list.forEach((item) => {
        const s = String(item || '').trim();
        if (!s) return;
        const lower = s.toLowerCase();
        // Match either by ID or by lowercased Romanian label.
        if (ALLERGEN_ID_TO_LABEL[lower]) ids.add(lower);
        else if (ALLERGEN_LABEL_TO_ID[lower]) ids.add(ALLERGEN_LABEL_TO_ID[lower]);
        else custom.push(s);
    });
    return { ids, custom };
}


const NUTRI_LABELS = [
    ['kcal', 'kcal'],
    ['kj', 'kJ'],
    ['fat_g', 'Grăsimi (g)'],
    ['saturated_g', '— saturate (g)'],
    ['carbs_g', 'Carbohidrați (g)'],
    ['sugars_g', '— zaharuri (g)'],
    ['protein_g', 'Proteine (g)'],
    ['salt_g', 'Sare (g)'],
];

const NUTRI_FIELDS = [
    { k: 'kcal', label: 'Energie (kcal)', step: '1' },
    { k: 'kj', label: 'Energie (kJ)', step: '1' },
    { k: 'fat_g', label: 'Grăsimi (g)', step: '0.1' },
    { k: 'saturated_g', label: '— din care saturate (g)', step: '0.1' },
    { k: 'carbs_g', label: 'Glucide (g)', step: '0.1' },
    { k: 'sugars_g', label: '— din care zaharuri (g)', step: '0.1' },
    { k: 'protein_g', label: 'Proteine (g)', step: '0.1' },
    { k: 'salt_g', label: 'Sare (g)', step: '0.01' },
];

function NutritionFields({ nutrition, onChange }) {
    const n = nutrition || {};
    const basis = n.basis === 'portion' ? 'portion' : '100g';
    const set = (patch) => onChange({ ...n, ...patch });
    const setField = (k, v) => set({ [k]: v === '' ? null : parseFloat(v) });
    return (
        <div style={{
            padding: '0.9rem 1rem',
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 10,
        }} data-testid="nutrition-fields">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                    Bază:
                </span>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: '0.85rem' }}>
                    <input type="radio" name="nutri-basis" checked={basis === '100g'} onChange={() => set({ basis: '100g', portion_grams: null })} data-testid="nutri-basis-100g" />
                    per 100 g
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: '0.85rem' }}>
                    <input type="radio" name="nutri-basis" checked={basis === 'portion'} onChange={() => set({ basis: 'portion' })} data-testid="nutri-basis-portion" />
                    per porție
                </label>
                {basis === 'portion' && (
                    <input
                        className="input"
                        type="number"
                        min={10}
                        max={5000}
                        placeholder="gramaj porție"
                        value={n.portion_grams ?? ''}
                        onChange={(e) => set({ portion_grams: e.target.value === '' ? null : parseFloat(e.target.value) })}
                        style={{ width: 130, padding: '0.35rem 0.5rem', fontSize: '0.85rem' }}
                        data-testid="nutri-portion-grams"
                    />
                )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
                {NUTRI_FIELDS.map(({ k, label, step }) => (
                    <div key={k}>
                        <label className="label" style={{ fontSize: '0.72rem' }}>{label}</label>
                        <input
                            className="input"
                            type="number"
                            min={0}
                            step={step}
                            value={n[k] ?? ''}
                            onChange={(e) => setField(k, e.target.value)}
                            data-testid={`nutri-${k}`}
                            style={{ padding: '0.4rem 0.55rem', fontSize: '0.88rem' }}
                        />
                    </div>
                ))}
            </div>
            {n.ai_generated_at && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-faint)', marginTop: 8 }}>
                    Generat AI {new Date(n.ai_generated_at).toLocaleDateString('ro-RO')} · valorile sunt editabile
                </div>
            )}
        </div>
    );
}

function NutritionAIBlock({ product, productName, ingredientsText, currentNutrition, onUpdated, onReloadNeeded }) {
    const [grams, setGrams] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(currentNutrition || product?.nutrition || null);
    const [usage, setUsage] = useState(null);
    const [open, setOpen] = useState(Boolean(currentNutrition || product?.nutrition));

    const generate = async () => {
        setError('');
        if (!ingredientsText?.trim()) {
            setError('Adaugă mai întâi ingredientele în câmpul de mai sus.');
            return;
        }
        setBusy(true);
        try {
            const payload = {
                ingredients_text: ingredientsText,
                product_name: productName || product?.name || '',
            };
            if (product?.id) payload.product_id = product.id;
            if (grams) payload.portion_grams = parseFloat(grams);
            const { data } = await api.post('/r/menu/nutrition/generate', payload);
            setResult(data.nutrition);
            setUsage(data.usage);
            if (onReloadNeeded && data.product) onReloadNeeded();
            if (onUpdated) onUpdated({ nutrition: data.nutrition, detected_allergens: data.detected_allergens || [], product: data.product });
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={{
            marginTop: 8,
            padding: '0.7rem 0.9rem',
            background: 'rgba(245, 158, 11, 0.05)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: 10,
        }} data-testid="nutrition-ai-block">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--amber)', fontSize: '0.88rem', fontWeight: 600, marginBottom: 6 }}>
                <Sparkles size={14} /> Generare automată din ingrediente
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 8px', lineHeight: 1.45 }}>
                Apasă <strong>Generează</strong> și AI completează câmpurile de mai sus pe baza ingredientelor scrise în câmpul „Ingrediente".
                Cu cantități („piept pui 120g, faină 80g") precizia e maximă; fără cantități AI estimează proporțiile tipice.
                Alergenii detectați se bifează automat în lista de mai jos. Toate valorile rămân editabile.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '160px auto', gap: 8, alignItems: 'end' }}>
                <div>
                    <label className="label" style={{ fontSize: '0.72rem' }}>Gramaj porție (opțional)</label>
                    <input className="input" type="number" min={10} max={5000} placeholder="ex. 350"
                           value={grams} onChange={e => setGrams(e.target.value)}
                           style={{ padding: '0.4rem 0.55rem', fontSize: '0.88rem' }}
                           data-testid="nutrition-portion-grams" />
                </div>
                <button type="button" className="btn btn-primary"
                        onClick={generate} disabled={busy || !ingredientsText?.trim()}
                        data-testid="nutrition-generate">
                    {busy ? <span className="spinner" /> : <><Sparkles size={14} /> Generează</>}
                </button>
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-faint)', marginTop: 6 }}>
                Fără gramaj → valori per 100 g. Cu gramaj → valori per porție.
            </div>
            {error && (
                <div style={{ color: 'var(--neon-magenta)', fontSize: '0.82rem', marginTop: 6 }}
                     data-testid="nutrition-error">{error}</div>
            )}
            {usage && usage.quota_monthly && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text-faint)', marginTop: 6 }}>
                    Generări AI folosite luna asta: <strong style={{ color: 'var(--text)' }}>{usage.used_this_month}</strong> / {usage.quota_monthly}
                </div>
            )}
        </div>
    );
}

function ProductForm({ initial, categories, menuLanguages, uploadConfigured, onImageChanged, onNutritionGenerated, onSave }) {
    const firstVariant = initial?.variants?.[0];
    const _initialAllergens = parseAllergens(initial?.allergens);
    const _initialTags = parseTags(initial?.tags);
    const [allergenIds, setAllergenIds] = useState(_initialAllergens.ids);
    const [customAllergens, setCustomAllergens] = useState(_initialAllergens.custom.join(', '));
    const [tagIds, setTagIds] = useState(_initialTags.ids);
    const [customTags, setCustomTags] = useState(_initialTags.custom.join(', '));
    const [form, setForm] = useState({
        ...initial,
        ingredients: (initial?.ingredients || []).join(', '),
        translations: initial?.translations || {},
        variants: initial?.variants?.length ? initial.variants : [{ name: '', price: '', weight: '' }],
    });

    const toggleAllergen = (id) => {
        setAllergenIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleTag = (id) => {
        setTagIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const [useVariants, setUseVariants] = useState(productUsesVariants(initial));
    const [simple, setSimple] = useState({
        price: firstVariant?.price ?? '',
        weight: firstVariant?.weight ?? '',
    });
    const [imageUrl, setImageUrl] = useState(initial?.image_url || null);
    const [pendingFile, setPendingFile] = useState(null);
    const [pendingPreview, setPendingPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        return () => {
            if (pendingPreview) URL.revokeObjectURL(pendingPreview);
        };
    }, [pendingPreview]);

    const previewUrl = pendingPreview || imageUrl;

    const setVariant = (i, key, val) => {
        const v = [...form.variants];
        v[i] = { ...v[i], [key]: val };
        setForm({ ...form, variants: v });
    };
    const addVariant = () => setForm({ ...form, variants: [...form.variants, { name: '', price: '', weight: '' }] });
    const removeVariant = (i) => setForm({ ...form, variants: form.variants.filter((_, idx) => idx !== i) });

    const buildVariants = () => {
        if (!useVariants) {
            const base = form.variants?.[0];
            return [{
                ...(base?.id ? { id: base.id } : {}),
                name: 'Porție',
                price: parseFloat(simple.price) || 0,
                weight: simple.weight?.trim() || null,
            }];
        }
        return form.variants
            .filter(v => (v.name || '').trim() || v.price !== '' && v.price != null)
            .map(v => ({
                ...(v.id ? { id: v.id } : {}),
                name: (v.name || '').trim() || 'Porție',
                price: parseFloat(v.price) || 0,
                weight: v.weight?.trim() || null,
            }));
    };

    const canSave = () => {
        if (!form.name?.trim() || !form.category_id) return false;
        if (!useVariants) return simple.price !== '' && !Number.isNaN(parseFloat(simple.price));
        const variants = buildVariants();
        return variants.length > 0 && variants.every(v => v.price > 0 && v.name);
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadError('');
        if (initial?.id) {
            setUploading(true);
            try {
                const fd = new FormData();
                fd.append('file', file);
                const { data } = await api.post(`/r/products/${initial.id}/image`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                if (pendingPreview) URL.revokeObjectURL(pendingPreview);
                setPendingFile(null);
                setPendingPreview(null);
                setImageUrl(data.image_url);
                onImageChanged && onImageChanged();
            } catch (err) {
                setUploadError(formatApiError(err));
            } finally {
                setUploading(false);
            }
        } else {
            if (pendingPreview) URL.revokeObjectURL(pendingPreview);
            setPendingFile(file);
            setPendingPreview(URL.createObjectURL(file));
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeImage = async () => {
        if (!previewUrl) return;
        if (!window.confirm('Ștergi imaginea curentă?')) return;
        if (initial?.id && imageUrl && !pendingFile) {
            await api.delete(`/r/products/${initial.id}/image`);
            setImageUrl(null);
            onImageChanged && onImageChanged();
            return;
        }
        if (pendingPreview) URL.revokeObjectURL(pendingPreview);
        setPendingFile(null);
        setPendingPreview(null);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave({
                ...form,
                id: initial?.id ?? form.id,
                image_url: imageUrl,
                tags: [
                    ...Array.from(tagIds).map((id) => TAG_ID_TO_LABEL[id]),
                    ...customTags.split(',').map((s) => s.trim()).filter(Boolean),
                ],
                // Save standard EU allergens as their Romanian label (so the client menu
                // displays them readable without an extra translation step), plus any custom.
                allergens: [
                    ...Array.from(allergenIds).map((id) => ALLERGEN_ID_TO_LABEL[id]),
                    ...customAllergens.split(',').map((s) => s.trim()).filter(Boolean),
                ],
                ingredients: form.ingredients.split(',').map(s => s.trim()).filter(Boolean),
                variants: buildVariants(),
                translations: form.translations || {},
            }, pendingFile);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            {/* Image uploader */}
            <div style={{ marginBottom: '1rem' }}>
                <label className="label">Imagine produs</label>
                {previewUrl ? (
                    <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', marginBottom: 8 }}>
                        <img src={previewUrl} alt="" style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} data-testid="prod-image-preview" />
                        <button type="button" className="btn btn-danger btn-sm" onClick={removeImage}
                                style={{ position: 'absolute', top: 8, right: 8 }} data-testid="prod-image-remove">
                            <Trash2 size={13} /> Șterge
                        </button>
                    </div>
                ) : (
                    <div style={{ height: 100, borderRadius: 12, background: 'var(--surface-2)', border: '1px dashed var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', marginBottom: 8, fontSize: '0.85rem' }}>
                        Fără imagine
                    </div>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFile}
                       style={{ display: 'none' }} data-testid="prod-image-input" />
                <button type="button" className="btn btn-secondary btn-sm"
                        disabled={!uploadConfigured || uploading}
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="prod-image-upload-btn">
                    {uploading ? <span className="spinner" /> : <Upload size={13} />}
                    {previewUrl ? ' Înlocuiește imaginea' : ' Încarcă imagine'}
                </button>
                {!uploadConfigured && <span style={{ marginLeft: 8, color: 'var(--text-faint)', fontSize: '0.8rem' }}>Supabase neconfigurat</span>}
                {pendingFile && !initial?.id && (
                    <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: '0.8rem' }}>Se încarcă la salvare</span>
                )}
                {uploadError && <div style={{ color: 'var(--neon-magenta)', fontSize: '0.82rem', marginTop: 6 }} data-testid="prod-upload-error">{uploadError}</div>}
            </div>

            <div style={{ marginBottom: '0.9rem' }}>
                <label className="label">Categorie</label>
                <select className="input" value={form.category_id || ''} onChange={(e) => setForm({ ...form, category_id: e.target.value })} data-testid="prod-category">
                    {categories.map(c => <option key={c.id} value={c.id}>{displayCategoryEmoji(c)} {c.name}</option>)}
                </select>
            </div>
            <div style={{ marginBottom: '0.9rem' }}>
                <label className="label">Nume</label>
                <input className="input" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prod-name" />
            </div>
            <div style={{ marginBottom: '0.9rem' }}>
                <label className="label">Descriere</label>
                <textarea className="textarea" rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="prod-description" />
            </div>
            <div style={{ marginBottom: '0.9rem' }}>
                <label className="label">Ingrediente</label>
                <input className="input" value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} placeholder="mozzarella 50g, roșii 80g, busuioc 5g" data-testid="prod-ingredients" />
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    Separate prin virgulă. Cu cantități („mozzarella 50g, roșii 80g") AI-ul de mai jos generează valori mai precise. Apar în meniul client, sub descriere.
                </div>
                <NutritionAIBlock
                    product={initial}
                    productName={form.name}
                    ingredientsText={form.ingredients}
                    currentNutrition={form.nutrition}
                    onUpdated={({ nutrition, detected_allergens, product: updatedProduct }) => {
                        if (nutrition) setForm((prev) => ({ ...prev, nutrition }));
                        const sourceAllergens = updatedProduct?.allergens
                            ?? [
                                ...Array.from(allergenIds).map((id) => ALLERGEN_ID_TO_LABEL[id]),
                                ...customAllergens.split(',').map((s) => s.trim()).filter(Boolean),
                                ...(detected_allergens || []),
                            ];
                        const parsed = parseAllergens(sourceAllergens);
                        setAllergenIds(parsed.ids);
                        setCustomAllergens(parsed.custom.join(', '));
                    }}
                    onReloadNeeded={onNutritionGenerated}
                />
            </div>

            <div style={{ marginBottom: '0.9rem' }}>
                <label className="label">Valori nutriționale</label>
                <NutritionFields
                    nutrition={form.nutrition}
                    onChange={(next) => setForm((prev) => ({ ...prev, nutrition: next }))}
                />
            </div>

            <div style={{ marginBottom: '0.9rem' }}>
                <label className="label">Etichete / Tag-uri</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }} data-testid="prod-tags-grid">
                    {POPULAR_TAGS.map((tag) => {
                        const active = tagIds.has(tag.id);
                        return (
                            <button
                                key={tag.id}
                                type="button"
                                onClick={() => toggleTag(tag.id)}
                                data-testid={`prod-tag-${tag.id}`}
                                style={{
                                    padding: '0.28rem 0.55rem',
                                    borderRadius: 999,
                                    border: `1px solid ${active ? 'var(--neon-teal)' : 'var(--border)'}`,
                                    background: active ? 'rgba(20,241,149,0.12)' : 'var(--surface-2)',
                                    color: active ? 'var(--neon-teal)' : 'var(--text-muted)',
                                    fontWeight: active ? 600 : 500,
                                    fontSize: '0.74rem',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    transition: 'all 0.12s',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <span>{tag.icon}</span>
                                <span>{tag.label}</span>
                                {active && <Check size={12} />}
                            </button>
                        );
                    })}
                </div>
                <div style={{ marginTop: 10 }}>
                    <input className="input"
                           value={customTags}
                           onChange={(e) => setCustomTags(e.target.value)}
                           placeholder='Alte etichete (opțional, separate cu virgulă: ex. "Specialitate săptămână")'
                           data-testid="prod-tags-custom" />
                    <div style={{ fontSize: '0.76rem', color: 'var(--text-faint)', marginTop: 4 }}>
                        Etichetele apar pe meniul clientului ca badge-uri sub numele produsului.
                    </div>
                </div>
            </div>
            <div style={{ marginBottom: '0.9rem' }}>
                <label className="label">Alergeni (cei 14 obligatorii UE)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }} data-testid="prod-allergens-grid">
                    {EU_ALLERGENS.map((a) => {
                        const active = allergenIds.has(a.id);
                        return (
                            <button
                                key={a.id}
                                type="button"
                                onClick={() => toggleAllergen(a.id)}
                                title={a.desc}
                                data-testid={`prod-allergen-${a.id}`}
                                style={{
                                    padding: '0.28rem 0.55rem',
                                    borderRadius: 999,
                                    border: `1px solid ${active ? 'var(--amber)' : 'var(--border)'}`,
                                    background: active ? 'rgba(245,158,11,0.15)' : 'var(--surface-2)',
                                    color: active ? 'var(--amber)' : 'var(--text-muted)',
                                    fontWeight: active ? 600 : 500,
                                    fontSize: '0.74rem',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    transition: 'all 0.12s',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <span>{a.icon}</span>
                                <span>{a.label}</span>
                                {active && <Check size={12} />}
                            </button>
                        );
                    })}
                </div>
                <div style={{ marginTop: 10 }}>
                    <input className="input"
                           value={customAllergens}
                           onChange={(e) => setCustomAllergens(e.target.value)}
                           placeholder="Alți alergeni (opțional, separați cu virgulă)"
                           data-testid="prod-allergens-custom" />
                    <div style={{ fontSize: '0.76rem', color: 'var(--text-faint)', marginTop: 4 }}>
                        Apasă o iconiță pentru a selecta. Cei 14 standard UE sunt obligatorii prin lege dacă produsul îi conține.
                    </div>
                </div>
            </div>

            <div style={{ marginBottom: '0.7rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.8rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={useVariants} onChange={e => setUseVariants(e.target.checked)} data-testid="prod-use-variants" />
                    <span>Folosesc variante (Mică / Medie / Mare)</span>
                </label>

                {!useVariants ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                            <label className="label">Preț (RON)</label>
                            <input className="input" type="number" step="0.5" min="0" placeholder="25"
                                   value={simple.price} onChange={e => setSimple(s => ({ ...s, price: e.target.value }))}
                                   data-testid="prod-simple-price" />
                        </div>
                        <div>
                            <label className="label">Gramaj</label>
                            <input className="input" placeholder="350g" value={simple.weight}
                                   onChange={e => setSimple(s => ({ ...s, weight: e.target.value }))}
                                   data-testid="prod-simple-weight" />
                        </div>
                    </div>
                ) : (
                    <>
                        <label className="label">Variante</label>
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 8 }}>Nume, preț și gramaj per variantă. Valorile nutriționale se setează o singură dată mai sus (acoperă tot produsul).</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 88px 90px auto', gap: 6, marginBottom: 4, fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            <span>Nume</span><span>Preț</span><span>Gramaj</span><span />
                        </div>
                        {form.variants.map((v, i) => (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 88px 90px auto', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                                <input className="input" placeholder="Mică / Medie" value={v.name || ''} onChange={(e) => setVariant(i, 'name', e.target.value)} data-testid={`var-name-${i}`} />
                                <input className="input" type="number" step="0.5" placeholder="Preț" value={v.price ?? ''} onChange={(e) => setVariant(i, 'price', e.target.value)} data-testid={`var-price-${i}`} />
                                <input className="input" placeholder="350g" value={v.weight || ''} onChange={(e) => setVariant(i, 'weight', e.target.value)} data-testid={`var-weight-${i}`} />
                                <button type="button" className="btn btn-danger btn-sm" onClick={() => removeVariant(i)}
                                        disabled={form.variants.length <= 1}
                                        title="Șterge varianta" aria-label="Șterge varianta" data-testid={`var-remove-${i}`}
                                        style={{ padding: '0.45rem 0.55rem', flexShrink: 0 }}>
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                        <button type="button" className="btn btn-secondary btn-sm" onClick={addVariant} data-testid="add-variant"><Plus size={13} /> Adaugă variantă</button>
                    </>
                )}
            </div>

            <MenuTranslationFields
                menuLanguages={menuLanguages}
                form={form}
                setForm={setForm}
                mode="product"
                variants={useVariants ? form.variants : buildVariants()}
            />

            <div style={{
                marginTop: '1rem',
                padding: '0.75rem 0.9rem',
                borderRadius: 10,
                border: '1px solid var(--border-faint)',
                background: 'var(--bg-faint, rgba(255,255,255,0.02))',
            }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={form.is_active !== false}
                        onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                        style={{ marginTop: 3 }}
                        data-testid="prod-is-active"
                    />
                    <span>
                        <strong style={{ display: 'block' }}>👁️ Produs vizibil în meniu</strong>
                        <span style={{ color: 'var(--text-faint)', fontSize: '0.82rem', lineHeight: 1.4 }}>
                            Debifează pentru a ascunde temporar produsul din meniul clienților (ex: produs sezonier, stoc epuizat). Rămâne în baza de date și poate fi reactivat oricând.
                        </span>
                    </span>
                </label>
            </div>

            <button className="btn btn-primary" style={{ width: '100%', marginTop: '0.9rem' }} disabled={!canSave() || saving} onClick={handleSave} data-testid="prod-save">
                {saving ? <span className="spinner" /> : null}
                {saving ? 'Se salvează...' : 'Salvează produsul'}
            </button>
        </div>
    );
}
